import React, { useState, useEffect, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { format, startOfWeek, addWeeks, subWeeks, getWeek, getYear, addDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, X, Copy, Check, Paperclip, Pin, StickyNote, Trash2, Moon, Sun, GripVertical, Search as SearchIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { generateDesignTerms } from '../services/gemini';
import { cn } from '../lib/utils';

const getImageUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  const domain = import.meta.env.VITE_IMAGE_DOMAIN || 'https://pub-e95b4d533ba043958d5b8a6d08375d8b.r2.dev';
  const baseUrl = domain.endsWith('/') ? domain.slice(0, -1) : domain;
  return `${baseUrl}/${url}`;
};

interface Tag {
  id: number;
  text: string;
}

interface ImageCardData {
  id: number;
  url: string;
  dayOfWeek: number;
  decorationType: string;
  rotation: number;
  width: number;
  tags: Tag[];
}

interface WeekData {
  id: number;
  year: number;
  weekNumber: number;
  notes: string;
  notesHeight: number;
  images: ImageCardData[];
}

interface SortableImageProps {
  img: ImageCardData;
  copiedTagId: number | null;
  copyToClipboard: (text: string, id: number) => void;
  deleteTag: (tagId: number, imageId: number) => Promise<void>;
  deleteImage: (imageId: number) => Promise<void>;
  updateImageWidth: (imageId: number, width: number) => Promise<void>;
  onPreview: (img: ImageCardData) => void;
  isDeleting: boolean;
  deletingTagIds: Set<number>;
}

interface DayCellProps {
  day: Date;
  dayOfWeek: number;
  label: string;
  weekData: WeekData | null;
  searchTerm: string;
  uploadingDays: number[];
  deletingImageIds: Set<number>;
  deletingTagIds: Set<number>;
  sensors: any;
  onDrop: (files: File[], dayOfWeek: number) => void;
  handlePaste: (e: React.ClipboardEvent, dayOfWeek: number) => void;
  handleDragEnd: (event: DragEndEvent, dayOfWeek: number) => void;
  copiedTagId: number | null;
  copyToClipboard: (text: string, id: number) => void;
  deleteTag: (tagId: number, imageId: number) => Promise<void>;
  deleteImage: (imageId: number) => Promise<void>;
  updateImageWidth: (imageId: number, width: number) => Promise<void>;
  onPreview: (img: ImageCardData) => void;
}

const arePropsEqual = (prevProps: any, nextProps: any) => {
  if (prevProps.img.id !== nextProps.img.id) return false;
  if (prevProps.img.width !== nextProps.img.width) return false;
  if (prevProps.copiedTagId !== nextProps.copiedTagId) return false;
  if (prevProps.isDeleting !== nextProps.isDeleting) return false;
  if (prevProps.img.tags.length !== nextProps.img.tags.length) return false;
  for (let i = 0; i < prevProps.img.tags.length; i++) {
    if (prevProps.img.tags[i].id !== nextProps.img.tags[i].id) return false;
  }
  if (prevProps.deletingTagIds.size !== nextProps.deletingTagIds.size) return false;
  for (const id of prevProps.deletingTagIds) {
    if (!nextProps.deletingTagIds.has(id)) return false;
  }
  return true;
};

const SortableImage = React.memo(({ img, copiedTagId, copyToClipboard, deleteTag, deleteImage, updateImageWidth, onPreview, isDeleting, deletingTagIds }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: img.id });

  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(img.width || 192);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isResizing ? 'none' : transition,
    zIndex: isDragging ? 50 : 1,
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = currentWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const newWidth = Math.max(120, Math.min(400, startWidth + deltaX));
      setCurrentWidth(newWidth);
    };

    const onMouseUp = () => {
      setIsResizing(false);
      updateImageWidth(img.id, currentWidth);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={cn("group relative", isDragging && "opacity-50")}
    >
      <div 
        style={{ rotate: `${img.rotation}deg`, width: currentWidth }}
        className={cn("polaroid cursor-pointer relative", isDeleting && "opacity-50 pointer-events-none")}
        onClick={() => !isResizing && !isDeleting && onPreview(img)}
      >
        {/* Deleting Loading Overlay */}
        {isDeleting && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-xl">
            <div className="flex flex-col items-center">
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="text-red-500 mb-2"
              >
                <Trash2 size={32} />
              </motion.div>
              <p className="font-hand text-red-600 text-sm">Deleting...</p>
            </div>
          </div>
        )}

        {/* Drag Handle */}
        <div 
          {...attributes} 
          {...listeners}
          className={cn(
            "absolute top-2 right-2 p-1 opacity-0 group-hover:opacity-100 bg-amber-100/80 rounded-md cursor-grab active:cursor-grabbing z-30 transition-opacity",
            isDeleting && "hidden"
          )}
        >
          <GripVertical size={14} className="text-amber-600" />
        </div>

        {/* Delete Image Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isDeleting) {
              deleteImage(img.id);
            }
          }}
          className={cn(
            "absolute top-2 left-2 p-1 opacity-0 group-hover:opacity-100 bg-red-100/80 hover:bg-red-200 rounded-md text-red-600 z-30 transition-opacity",
            isDeleting && "hidden"
          )}
        >
          <Trash2 size={14} />
        </button>

        {/* Resize Handle */}
        <div 
          onMouseDown={handleResizeMouseDown}
          className="absolute bottom-1 right-1 w-4 h-4 cursor-nwse-resize opacity-0 group-hover:opacity-100 transition-opacity z-30 flex items-center justify-center"
        >
          <div className="w-2 h-2 border-r-2 border-b-2 border-amber-400" />
        </div>

        <div className={img.decorationType} />
        <div className="relative">
          <img 
            src={getImageUrl(img.url)} 
            alt="Inspiration" 
            className="w-full h-auto object-cover mb-2 pointer-events-none"
            referrerPolicy="no-referrer" 
            loading="lazy"
          />
        </div>
        
        {/* Tags Overlay */}
        <div className="absolute -right-4 top-10 flex flex-col gap-1 z-20">
          {img.tags.length > 0 && (
            <div className="relative group/tags">
              <motion.div 
                className="bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-[10px] font-bold shadow-sm border border-amber-200 flex items-center gap-1 whitespace-nowrap"
                whileHover={{ scale: 1.05 }}
              >
                {img.tags[0].text}
                {img.tags.length > 1 && (
                  <span className="bg-amber-200 px-1 rounded-full">+{img.tags.length - 1}</span>
                )}
              </motion.div>

              {/* Expanded Tags */}
              <div className="absolute left-0 top-0 opacity-0 group-hover/tags:opacity-100 pointer-events-none group-hover/tags:pointer-events-auto transition-opacity flex flex-col gap-1 bg-white/90 backdrop-blur-md p-2 rounded-xl shadow-xl border border-amber-100 z-30 min-w-[120px] dark:bg-amber-900/90 dark:border-amber-800">
                {img.tags.map(tag => {
                  const isDeletingTag = deletingTagIds.has(tag.id);
                  return (
                    <div key={tag.id} className={cn("flex items-center justify-between group/tag", isDeletingTag && "opacity-50 pointer-events-none")}>
                      <button 
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(tag.text, tag.id); }}
                        className="text-[10px] hover:text-amber-600 transition-colors flex items-center gap-1 flex-1 text-left"
                      >
                        {copiedTagId === tag.id ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
                        {tag.text}
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); deleteTag(tag.id, img.id); }}
                        className="opacity-0 group-hover/tag:opacity-100 p-1 hover:bg-red-100 rounded-full text-red-500 transition-all"
                      >
                        {isDeletingTag ? (
                          <motion.div 
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <X size={10} />
                          </motion.div>
                        ) : (
                          <X size={10} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}, arePropsEqual);

const DayCell = React.memo(({ 
  day, 
  dayOfWeek, 
  label, 
  weekData, 
  searchTerm, 
  uploadingDays, 
  deletingImageIds, 
  deletingTagIds, 
  sensors, 
  onDrop, 
  handlePaste, 
  handleDragEnd, 
  copiedTagId, 
  copyToClipboard, 
  deleteTag, 
  deleteImage, 
  updateImageWidth, 
  onPreview 
}: DayCellProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files: File[]) => onDrop(files, dayOfWeek),
    accept: { 'image/*': [] },
    noClick: true,
    multiple: false
  } as any);

  const [uploadStartTime, setUploadStartTime] = React.useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = React.useState(0);

  React.useEffect(() => {
    const isUploading = uploadingDays.includes(dayOfWeek);
    
    if (isUploading && !uploadStartTime) {
      setUploadStartTime(Date.now());
      setElapsedMs(0);
    } else if (!isUploading && uploadStartTime) {
      setUploadStartTime(null);
      setElapsedMs(0);
    }
  }, [uploadingDays, dayOfWeek, uploadStartTime]);

  React.useEffect(() => {
    if (!uploadStartTime) return;
    
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - uploadStartTime);
    }, 10);
    
    return () => clearInterval(interval);
  }, [uploadStartTime]);

  const dayImages = React.useMemo(() => {
    return (weekData?.images || []).filter(img => {
      if (img.dayOfWeek !== dayOfWeek) return false;
      if (!searchTerm) return true;
      
      const searchLower = searchTerm.toLowerCase();
      const tags = img.tags || [];
      const matchesTag = tags.some(tag => tag?.text?.toLowerCase().includes(searchLower));
      const matchesNotes = (weekData?.notes || '').toLowerCase().includes(searchLower);
      
      return matchesTag || matchesNotes;
    });
  }, [weekData?.images, dayOfWeek, searchTerm, weekData?.notes]);

  const isUploading = uploadingDays.includes(dayOfWeek);

  return (
    <div 
      {...getRootProps()} 
      onPaste={(e) => handlePaste(e, dayOfWeek)}
      tabIndex={0}
      className={cn(
        "relative min-h-[300px] border-2 border-dashed border-amber-200 rounded-2xl p-4 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400/50",
        isDragActive ? "bg-amber-100 border-amber-400 scale-[0.98]" : "bg-white/40 hover:bg-white/60",
        isUploading && "opacity-70 cursor-wait",
        "dark:border-amber-800 dark:bg-amber-900/20 dark:hover:bg-amber-900/40"
      )}
    >
      <input {...getInputProps()} />
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-hand text-2xl text-amber-800 dark:text-amber-200">{label}</h3>
        <span className="text-xs font-mono text-amber-600/60 dark:text-amber-400/60">{format(day, 'MMM dd')}</span>
      </div>

      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => handleDragEnd(event, dayOfWeek)}
      >
        <SortableContext 
          items={dayImages.map(img => img.id)}
          strategy={rectSortingStrategy}
        >
          <div className="flex flex-wrap gap-8 justify-center">
            {dayImages.map((img) => (
              <SortableImage 
                key={img.id} 
                img={img} 
                copiedTagId={copiedTagId}
                copyToClipboard={copyToClipboard}
                deleteTag={deleteTag}
                deleteImage={deleteImage}
                updateImageWidth={updateImageWidth}
                onPreview={onPreview}
                isDeleting={deletingImageIds.has(img.id)}
                deletingTagIds={deletingTagIds}
              />
            ))}

            {isUploading && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="polaroid flex flex-col items-center justify-center min-h-[150px] w-48 bg-white/80 backdrop-blur-sm border-2 border-dashed border-amber-300"
              >
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="text-amber-500 mb-2"
                >
                  <StickyNote size={32} />
                </motion.div>
                <p className="font-hand text-amber-600">AI Analyzing...</p>
                <p className="font-mono text-xs text-amber-400 mt-1">
                  {elapsedMs >= 1000 
                    ? `${(elapsedMs / 1000).toFixed(3)} s` 
                    : `${elapsedMs}ms`}
                </p>
              </motion.div>
            )}
            
            {dayImages.length === 0 && !isUploading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-amber-300 dark:text-amber-800 pointer-events-none">
                <Plus size={48} strokeWidth={1} />
                <p className="font-hand text-lg mt-2">Paste or Drop Image</p>
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
});

export default function App() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekData, setWeekData] = useState<WeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [copiedTagId, setCopiedTagId] = useState<number | null>(null);
  const [previewImage, setPreviewImage] = useState<ImageCardData | null>(null);
  const [aiPrompt, setAiPrompt] = useState<string | null>(null);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasCopiedPrompt, setHasCopiedPrompt] = useState(false);
  const [uploadingDays, setUploadingDays] = useState<number[]>([]);
  const [deletingImageIds, setDeletingImageIds] = useState<Set<number>>(new Set());
  const [deletingTagIds, setDeletingTagIds] = useState<Set<number>>(new Set());
  const fetchedKeysRef = React.useRef<Set<string>>(new Set());
  const imageCacheRef = React.useRef<Map<number, ImageCardData>>(new Map());

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 6);
  const weekNumber = getWeek(currentDate);
  const year = getYear(currentDate);

  const mergeWithCache = (images: any[]): ImageCardData[] => {
    return images.map((img: any) => {
      const mappedImg = {
        ...img,
        dayOfWeek: img.day_of_week,
        decorationType: img.decoration_type,
        weekId: img.week_id,
        tags: img.tags || []
      };
      
      const cachedImg = imageCacheRef.current.get(mappedImg.id);
      if (cachedImg) {
        const hasChanged = 
          cachedImg.width !== mappedImg.width ||
          JSON.stringify(cachedImg.tags) !== JSON.stringify(mappedImg.tags);
        
        if (!hasChanged) {
          return cachedImg;
        }
      }
      
      imageCacheRef.current.set(mappedImg.id, mappedImg);
      return mappedImg;
    });
  };

  useEffect(() => {
    const currentKey = `${year}-${weekNumber}`;
    
    if (fetchedKeysRef.current.has(currentKey)) {
      return;
    }
    
    fetchedKeysRef.current.add(currentKey);
    setLoading(true);
    
    const fetchData = async () => {
      try {
        const res = await fetch(`/api/week/${year}/${weekNumber}`);
        const data = await res.json();
        
        const mappedData = {
          ...data,
          weekNumber: data.week_number,
          notesHeight: data.notes_height,
          images: mergeWithCache(data.images || [])
        };
        
        setWeekData(mappedData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [year, weekNumber]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));

  const onDrop = async (acceptedFiles: File[], dayOfWeek: number) => {
    if (!weekData) return;
    console.log("[Scrapbook] Starting upload for day:", dayOfWeek);
    setUploadingDays(prev => [...prev, dayOfWeek]);

    const file = acceptedFiles[0];
    
    try {
      // 1. Image Compression
      console.log("[Scrapbook] Compressing image...");
      const options = {
        maxSizeMB: 0.5, // 500KB
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);
      console.log(`[Scrapbook] Compressed size: ${compressedFile.size / 1024 / 1024} MB`);

      // 2. Get Presigned URL
      console.log("[Scrapbook] Getting presigned URL...");
      const presignedRes = await fetch('/api/upload/presigned-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: compressedFile.type })
      });
      if (!presignedRes.ok) throw new Error("Failed to get presigned URL");
      const { signedUrl, key } = await presignedRes.json();

      // 3. Upload to R2
      console.log("[Scrapbook] Uploading to R2...");
      const uploadRes = await fetch(signedUrl, {
        method: 'PUT',
        body: compressedFile,
        headers: { 'Content-Type': compressedFile.type }
      });
      if (!uploadRes.ok) throw new Error("Failed to upload to R2");

      const imageUrl = getImageUrl(key);

      // 4. AI Analysis
      console.log("[Scrapbook] Calling AI analysis with URL:", imageUrl);
      const aiTerms = await generateDesignTerms(imageUrl);
      console.log("[Scrapbook] AI analysis results:", aiTerms);
      
      const decorationTypes = ['tape', 'pin', 'clip'];
      const decorationType = decorationTypes[Math.floor(Math.random() * decorationTypes.length)];
      const rotation = Math.floor(Math.random() * 10) - 5;

      // 5. Save to Database (Key only)
      console.log("[Scrapbook] Saving key to database:", key);
      const res = await fetch('/api/images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weekId: weekData.id,
          dayOfWeek,
          url: key, // We store the key in the url column
          decorationType,
          rotation,
          tags: aiTerms
        })
      });

      if (!res.ok) throw new Error(`Database save failed: ${res.status}`);

      const rawImage = await res.json();
      console.log("[Scrapbook] New image saved successfully:", rawImage);

      // 字段映射：将下划线命名转换为小驼峰
      const newImage = {
        ...rawImage,
        dayOfWeek: rawImage.day_of_week,
        decorationType: rawImage.decoration_type,
        weekId: rawImage.week_id,
        tags: rawImage.tags || []
      };

      imageCacheRef.current.set(newImage.id, newImage);

      setWeekData(prev => {
        if (!prev) return null;
        console.log("[Scrapbook] Updating local state with mapped image:", newImage);
        return {
          ...prev,
          images: [...prev.images, newImage]
        };
      });
    } catch (err) {
      console.error("[Scrapbook] Error during upload process:", err);
      let errorMessage = "Unknown error";
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      }
      alert("Upload failed: " + errorMessage);
    } finally {
      setUploadingDays(prev => prev.filter(d => d !== dayOfWeek));
    }
  };

  const deleteTag = React.useCallback(async (tagId: number, imageId: number) => {
    setDeletingTagIds(prev => new Set([...prev, tagId]));
    
    try {
      await fetch(`/api/tags/${tagId}`, { method: 'DELETE' });
      setWeekData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          images: prev.images.map(img => {
            if (img.id === imageId) {
              const updatedImg = { ...img, tags: img.tags.filter(t => t.id !== tagId) };
              imageCacheRef.current.set(imageId, updatedImg);
              return updatedImg;
            }
            return img;
          })
        };
      });
    } finally {
      setDeletingTagIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(tagId);
        return newSet;
      });
    }
  }, []);

  const deleteImage = React.useCallback(async (imageId: number) => {
    if (!confirm('Are you sure you want to delete this inspiration?')) return;
    
    // 标记为删除中
    setDeletingImageIds(prev => new Set([...prev, imageId]));
    
    try {
      await fetch(`/api/images/${imageId}`, { method: 'DELETE' });
      imageCacheRef.current.delete(imageId);
      setWeekData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          images: prev.images.filter(img => img.id !== imageId)
        };
      });
    } finally {
      // 删除完成后移除标记
      setDeletingImageIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  }, []);

  const copyToClipboard = React.useCallback(async (text: string, tagId: number | string) => {
    const performCopy = async () => {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for non-secure contexts or missing clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          return successful;
        } catch (err) {
          document.body.removeChild(textArea);
          return false;
        }
      }
    };

    const success = await performCopy();
    if (success) {
      if (typeof tagId === 'number') {
        setCopiedTagId(tagId);
        setTimeout(() => setCopiedTagId(null), 2000);
      } else if (tagId === 'ai-prompt') {
        setHasCopiedPrompt(true);
        setTimeout(() => setHasCopiedPrompt(false), 2000);
      }
    }
  }, []);

  const handlePaste = async (e: React.ClipboardEvent, dayOfWeek: number) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          onDrop([file], dayOfWeek);
        }
      }
    }
  };

  const updateNotes = async (notes: string) => {
    if (!weekData) return;
    setWeekData({ ...weekData, notes });
    await fetch(`/api/weeks/${weekData.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes })
    });
  };

  const updateNotesHeight = async (height: number) => {
    if (!weekData) return;
    setWeekData({ ...weekData, notesHeight: height });
    await fetch(`/api/weeks/${weekData.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notesHeight: height })
    });
  };

  const updateImageWidth = React.useCallback(async (imageId: number, width: number) => {
    setWeekData(prev => {
      if (!prev) return null;
      return {
        ...prev,
        images: prev.images.map(img => {
          if (img.id === imageId) {
            const updatedImg = { ...img, width };
            imageCacheRef.current.set(imageId, updatedImg);
            return updatedImg;
          }
          return img;
        })
      };
    });
    await fetch(`/api/images/${imageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ width })
    });
  }, []);

  const generateAiPrompt = React.useCallback(async (imageUrl: string) => {
    setIsGeneratingPrompt(true);
    try {
      const prompt = await generateDesignTerms(imageUrl, true); // Pass true for full prompt
      setAiPrompt(prompt[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setIsGeneratingPrompt(false);
    }
  }, []);

  useEffect(() => {
    if (previewImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [previewImage]);

  const onPreview = React.useCallback((img: ImageCardData) => {
    setPreviewImage(img);
    setAiPrompt(null);
    generateAiPrompt(getImageUrl(img.url));
  }, [generateAiPrompt]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent, dayOfWeek: number) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !weekData) return;

    const dayImages = weekData.images.filter(img => img.dayOfWeek === dayOfWeek);
    const oldIndex = dayImages.findIndex(img => img.id === Number(active.id));
    const newIndex = dayImages.findIndex(img => img.id === Number(over.id));

    if (oldIndex === -1 || newIndex === -1) return;

    const newDayImages = arrayMove(dayImages, oldIndex, newIndex) as ImageCardData[];
    
    // Update local state
    const otherImages = weekData.images.filter(img => img.dayOfWeek !== dayOfWeek);
    const updatedImages = [...otherImages, ...newDayImages];
    
    setWeekData({ ...weekData, images: updatedImages });

    // Persist to DB
    const reorderPayload = newDayImages.map((img: ImageCardData, index: number) => ({
      id: img.id,
      position: index
    }));

    await fetch('/api/images/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageIds: reorderPayload })
    });
  };

  if (loading && !weekData) {
    return (
      <div className="h-screen flex items-center justify-center bg-amber-50 dark:bg-amber-950">
        <motion.div 
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="text-amber-500"
        >
          <StickyNote size={48} />
        </motion.div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen p-6 md:p-12 max-w-7xl mx-auto", isDarkMode && "dark")}>
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
        <div className="flex items-center gap-4">
          <div className="bg-amber-200 dark:bg-amber-800 p-3 rounded-2xl rotate-3 shadow-lg">
            <StickyNote className="text-amber-700 dark:text-amber-200" size={32} />
          </div>
          <div>
            <h1 className="text-4xl font-hand text-amber-900 dark:text-amber-50">Design Scrapbook</h1>
            <p className="text-amber-600 dark:text-amber-400 font-journal">Capturing inspiration, one week at a time.</p>
          </div>
        </div>

        <div className="flex items-center gap-6 bg-white/60 dark:bg-amber-900/40 backdrop-blur-md px-6 py-3 rounded-full shadow-sm border border-amber-100 dark:border-amber-800">
          <button onClick={handlePrevWeek} className="p-2 hover:bg-amber-100 dark:hover:bg-amber-800 rounded-full transition-colors">
            <ChevronLeft size={20} />
          </button>
          <div className="text-center min-w-[180px]">
            <div className="text-xs uppercase tracking-widest font-bold text-amber-500">Week {weekNumber} • {year}</div>
            <div className="font-hand text-xl">{format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d')}</div>
          </div>
          <button onClick={handleNextWeek} className="p-2 hover:bg-amber-100 dark:hover:bg-amber-800 rounded-full transition-colors">
            <ChevronRight size={20} />
          </button>
        </div>

        <div 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="p-3 bg-white/60 dark:bg-amber-900/40 rounded-2xl shadow-sm border border-amber-100 dark:border-amber-800 hover:scale-110 transition-transform cursor-pointer"
        >
          {isDarkMode ? <Sun className="text-amber-200" /> : <Moon className="text-amber-700" />}
        </div>
      </header>

      {/* Search Bar */}
      <div className="mb-8 relative max-w-md mx-auto">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <SearchIcon size={18} className="text-amber-400" />
        </div>
        <input 
          type="text"
          placeholder="Search design terms or notes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white/60 dark:bg-amber-900/40 backdrop-blur-md rounded-2xl border border-amber-100 dark:border-amber-800 focus:ring-2 focus:ring-amber-400/50 focus:outline-none font-journal text-lg shadow-sm placeholder:text-amber-300 dark:placeholder:text-amber-800"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="absolute inset-y-0 right-0 pr-4 flex items-center text-amber-400 hover:text-amber-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Grid Layout */}
      <main className="space-y-6">
        {/* Row 1: Mon, Tue, Wed */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DayCell 
            day={weekStart} 
            dayOfWeek={1} 
            label="Monday" 
            weekData={weekData}
            searchTerm={searchTerm}
            uploadingDays={uploadingDays}
            deletingImageIds={deletingImageIds}
            deletingTagIds={deletingTagIds}
            sensors={sensors}
            onDrop={onDrop}
            handlePaste={handlePaste}
            handleDragEnd={handleDragEnd}
            copiedTagId={copiedTagId}
            copyToClipboard={copyToClipboard}
            deleteTag={deleteTag}
            deleteImage={deleteImage}
            updateImageWidth={updateImageWidth}
            onPreview={onPreview}
          />
          <DayCell 
            day={addDays(weekStart, 1)} 
            dayOfWeek={2} 
            label="Tuesday" 
            weekData={weekData}
            searchTerm={searchTerm}
            uploadingDays={uploadingDays}
            deletingImageIds={deletingImageIds}
            deletingTagIds={deletingTagIds}
            sensors={sensors}
            onDrop={onDrop}
            handlePaste={handlePaste}
            handleDragEnd={handleDragEnd}
            copiedTagId={copiedTagId}
            copyToClipboard={copyToClipboard}
            deleteTag={deleteTag}
            deleteImage={deleteImage}
            updateImageWidth={updateImageWidth}
            onPreview={onPreview}
          />
          <DayCell 
            day={addDays(weekStart, 2)} 
            dayOfWeek={3} 
            label="Wednesday" 
            weekData={weekData}
            searchTerm={searchTerm}
            uploadingDays={uploadingDays}
            deletingImageIds={deletingImageIds}
            deletingTagIds={deletingTagIds}
            sensors={sensors}
            onDrop={onDrop}
            handlePaste={handlePaste}
            handleDragEnd={handleDragEnd}
            copiedTagId={copiedTagId}
            copyToClipboard={copyToClipboard}
            deleteTag={deleteTag}
            deleteImage={deleteImage}
            updateImageWidth={updateImageWidth}
            onPreview={onPreview}
          />
        </div>

        {/* Row 2: Thu, Fri, Weekend */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <DayCell 
            day={addDays(weekStart, 3)} 
            dayOfWeek={4} 
            label="Thursday" 
            weekData={weekData}
            searchTerm={searchTerm}
            uploadingDays={uploadingDays}
            deletingImageIds={deletingImageIds}
            deletingTagIds={deletingTagIds}
            sensors={sensors}
            onDrop={onDrop}
            handlePaste={handlePaste}
            handleDragEnd={handleDragEnd}
            copiedTagId={copiedTagId}
            copyToClipboard={copyToClipboard}
            deleteTag={deleteTag}
            deleteImage={deleteImage}
            updateImageWidth={updateImageWidth}
            onPreview={onPreview}
          />
          <DayCell 
            day={addDays(weekStart, 4)} 
            dayOfWeek={5} 
            label="Friday" 
            weekData={weekData}
            searchTerm={searchTerm}
            uploadingDays={uploadingDays}
            deletingImageIds={deletingImageIds}
            deletingTagIds={deletingTagIds}
            sensors={sensors}
            onDrop={onDrop}
            handlePaste={handlePaste}
            handleDragEnd={handleDragEnd}
            copiedTagId={copiedTagId}
            copyToClipboard={copyToClipboard}
            deleteTag={deleteTag}
            deleteImage={deleteImage}
            updateImageWidth={updateImageWidth}
            onPreview={onPreview}
          />
          <div className="md:col-span-1">
             <DayCell 
              day={addDays(weekStart, 5)} 
              dayOfWeek={6} 
              label="Weekend" 
              weekData={weekData}
              searchTerm={searchTerm}
              uploadingDays={uploadingDays}
              deletingImageIds={deletingImageIds}
              deletingTagIds={deletingTagIds}
              sensors={sensors}
              onDrop={onDrop}
              handlePaste={handlePaste}
              handleDragEnd={handleDragEnd}
              copiedTagId={copiedTagId}
              copyToClipboard={copyToClipboard}
              deleteTag={deleteTag}
              deleteImage={deleteImage}
              updateImageWidth={updateImageWidth}
              onPreview={onPreview}
            />
          </div>
        </div>

        {/* Row 3: Notes Area */}
        <motion.div 
          className="relative bg-amber-100/50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-3xl p-8 overflow-hidden"
          style={{ height: weekData?.notesHeight || 240 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <StickyNote size={20} className="text-amber-600" />
            <h3 className="font-hand text-2xl text-amber-800 dark:text-amber-200">Weekly Notes</h3>
          </div>
          <textarea 
            value={weekData?.notes || ''}
            onChange={(e) => updateNotes(e.target.value)}
            placeholder="Scribble your thoughts here..."
            className="w-full h-[calc(100%-4rem)] bg-transparent border-none focus:ring-0 font-journal text-xl resize-none placeholder:text-amber-300 dark:placeholder:text-amber-800"
          />
          
          {/* Resize Handle */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize hover:bg-amber-200/50 transition-colors flex items-center justify-center"
            onMouseDown={(e) => {
              const startY = e.clientY;
              const startHeight = weekData?.notesHeight || 240;
              const onMouseMove = (moveEvent: MouseEvent) => {
                const newHeight = Math.max(150, Math.min(600, startHeight + (moveEvent.clientY - startY)));
                updateNotesHeight(newHeight);
              };
              const onMouseUp = () => {
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
              };
              window.addEventListener('mousemove', onMouseMove);
              window.addEventListener('mouseup', onMouseUp);
            }}
          >
            <div className="w-12 h-1 bg-amber-300 rounded-full" />
          </div>
        </motion.div>
      </main>

      <footer className="mt-12 text-center text-amber-400 dark:text-amber-800 font-journal text-sm">
        <p>© {year} Design Scrapbook • Built with Gemini AI</p>
      </footer>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4 md:p-12 overflow-hidden"
            onClick={() => setPreviewImage(null)}
          >
            <motion.button 
              className="absolute top-6 right-6 text-white/60 hover:text-white p-2 z-50"
              onClick={() => setPreviewImage(null)}
            >
              <X size={32} />
            </motion.button>

            <div className="flex flex-col md:flex-row gap-8 max-w-6xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex-1 flex items-center justify-center overflow-hidden">
                <img 
                  src={getImageUrl(previewImage.url)} 
                  alt="Preview" 
                  className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                />
              </div>

              <div className="w-full md:w-80 flex flex-col gap-4 bg-white/10 backdrop-blur-md p-6 rounded-3xl border border-white/10 max-h-[80vh] overflow-y-auto">
                <h3 className="text-white font-hand text-2xl pb-2">AI Reasoning</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-widest font-bold">Design Tags</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {previewImage.tags.map(tag => (
                        <span key={tag.id} className="bg-amber-100/20 text-amber-200 px-2 py-1 rounded-full text-xs">
                          {tag.text}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-widest font-bold">AI Drawing Prompt</label>
                    <div className="mt-2 bg-black/40 p-4 rounded-xl border border-white/5 min-h-[100px] relative">
                      {isGeneratingPrompt ? (
                        <div className="flex items-center justify-center h-full text-white/40 italic text-sm">
                          Reasoning...
                        </div>
                      ) : (
                        <>
                          <p className="text-white/80 text-sm font-journal leading-relaxed">
                            {aiPrompt || "No prompt generated."}
                          </p>
                          {aiPrompt && (
                            <button 
                              onClick={() => copyToClipboard(aiPrompt, 'ai-prompt')}
                              className={cn(
                                "absolute bottom-2 right-2 p-2 rounded-lg transition-all",
                                hasCopiedPrompt 
                                  ? "bg-green-500/20 text-green-400" 
                                  : "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
                              )}
                              title="Copy Prompt"
                            >
                              {hasCopiedPrompt ? <Check size={16} /> : <Copy size={16} />}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setPreviewImage(null)}
                  className="mt-4 w-full py-3 bg-white text-black rounded-2xl font-bold hover:bg-amber-100 transition-colors sticky bottom-0"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
